// Shared helpers for the Home page block layout.
//
// The layout lives in DashboardSettings.sections as an ordered array; array
// order is render order. Both the public Home page and the admin builder read
// it through these helpers so they can never disagree on how a block resolves.

export const SECTION_LABELS = {
  hero: 'Hero slider',
  categoryRow: 'Category row',
  productRow: 'Picked products',
  banner: 'Banner / CTA',
  richText: 'Text',
  spacer: 'Spacer',
};

/** Ids only need to be unique inside one layout, not globally. */
export const newSectionId = (type) =>
  `${type}-${Math.random().toString(36).slice(2, 9)}`;

// Empty colour and a scale of 1 mean "inherit the theme"; see blockTextStyle.
const TEXT_DEFAULTS = { textColor: '', textScale: 1 };

/** A fresh block of each type, with the same defaults the backend applies. */
export const blockDefaults = {
  hero: () => ({ id: newSectionId('hero'), type: 'hero', enabled: true, ...TEXT_DEFAULTS }),

  categoryRow: (category) => ({
    id: newSectionId('cat'),
    type: 'categoryRow',
    enabled: true,
    ...TEXT_DEFAULTS,
    categoryId: String(category._id),
    categoryName: category.name,
    titleOverride: '',
    featuredStyle: false,
  }),

  productRow: () => ({
    id: newSectionId('row'),
    type: 'productRow',
    enabled: true,
    ...TEXT_DEFAULTS,
    titleOverride: 'New row',
    featuredStyle: false,
    productIds: [],
  }),

  banner: () => ({
    id: newSectionId('banner'),
    type: 'banner',
    enabled: true,
    ...TEXT_DEFAULTS,
    mediaType: 'image',
    mediaUrl: '',
    backgroundColor: '#141414',
    overlay: 0.4,
    // Empty on purpose: a new banner must not ship placeholder copy that ends
    // up published because nobody noticed it was there.
    heading: '',
    body: '',
    buttonText: '',
    linkType: 'none',
    linkValue: '',
    height: 'medium',
    align: 'left',
  }),

  richText: () => ({
    id: newSectionId('text'),
    type: 'richText',
    enabled: true,
    ...TEXT_DEFAULTS,
    heading: '',
    contentFormat: 'text',
    content: '',
    align: 'left',
    maxWidth: 720,
  }),

  spacer: () => ({
    id: newSectionId('spacer'),
    type: 'spacer',
    enabled: true,
    spacerHeight: 48,
    divider: false,
  }),
};

/**
 * The layout an untouched install renders: hero first, then every category with
 * "featured" on top. Mirrors buildDefaultSections in the backend controller and
 * is only used as a client-side fallback when the API gives us nothing.
 */
export const buildDefaultSections = (categories = []) => {
  const sorted = [...categories].sort((a, b) => {
    if (a.name === 'featured') return -1;
    if (b.name === 'featured') return 1;
    return 0;
  });

  return [
    { id: 'hero', type: 'hero', enabled: true },
    ...sorted.map((cat) => ({
      id: `cat-${cat._id}`,
      type: 'categoryRow',
      enabled: true,
      categoryId: String(cat._id),
      categoryName: cat.name,
      titleOverride: '',
      featuredStyle: cat.name === 'featured',
    })),
  ];
};

/** Match a stored block against the live category list: id first, name as fallback. */
export const findCategoryForSection = (section, categories = []) =>
  categories.find((cat) => String(cat._id) === String(section.categoryId)) ||
  categories.find((cat) => cat.name === section.categoryName) ||
  null;

/**
 * Where a banner button points. Product ids and landing slugs become in-app
 * paths; the free-form option is passed through as-is (the backend already
 * rejected anything that is not http(s) or a site-relative path).
 */
export const bannerHref = (section) => {
  if (!section.linkValue) return null;
  switch (section.linkType) {
    case 'product': return `/product/${section.linkValue}`;
    case 'landing': return `/${String(section.linkValue).replace(/^\//, '')}`;
    case 'url': return section.linkValue;
    default: return null;
  }
};

/** True when the href should be handled by the router instead of a full reload. */
export const isInternalHref = (href) => !!href && href.startsWith('/');

/** Blocks that render text and therefore expose the colour / size controls. */
export const TEXT_STYLED_TYPES = ['hero', 'categoryRow', 'productRow', 'banner', 'richText'];

/**
 * Per-block text styling, handed to the DOM as inherited CSS custom properties.
 * The stylesheets read them as `var(--block-text-color, <original value>)` and
 * `calc(<original size> * var(--block-text-scale, 1))`, so a block that sets
 * neither renders byte-identically to how it did before these existed.
 *
 * Only emit a property when it is actually set - an empty custom property would
 * override the fallback with nothing and blank the text out.
 */
export const blockTextStyle = (section) => {
  const style = {};
  if (section.textColor) style['--block-text-color'] = section.textColor;
  if (section.textScale && section.textScale !== 1) style['--block-text-scale'] = section.textScale;
  return style;
};

/**
 * Turn stored blocks into what should actually be rendered: drops disabled
 * blocks and blocks whose content has gone missing (a deleted category, a row
 * whose products are all unpublished), and attaches the resolved data so
 * callers don't have to look it up again.
 *
 * `includeDisabled` is what the builder passes, so the admin keeps seeing the
 * hidden and empty blocks it needs to be able to fix them.
 */
export const resolveSections = (sections, context = {}, { includeDisabled = false } = {}) => {
  if (!Array.isArray(sections)) return [];
  const { categories = [], productsById = new Map() } = context;

  return sections.reduce((acc, section) => {
    if (!includeDisabled && section.enabled === false) return acc;

    if (section.type === 'categoryRow') {
      const category = findCategoryForSection(section, categories);
      if (!category && !includeDisabled) return acc;
      acc.push({ ...section, category, products: category?.products || [] });
      return acc;
    }

    if (section.type === 'productRow') {
      const products = (section.productIds || [])
        .map((id) => productsById.get(String(id)))
        .filter(Boolean);
      if (products.length === 0 && !includeDisabled) return acc;
      acc.push({ ...section, category: null, products });
      return acc;
    }

    acc.push({ ...section, category: null, products: [] });
    return acc;
  }, []);
};

/** Move an item inside an array, returning a new array. Used by the drag & drop. */
export const moveItem = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};
